import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMercadoPago } from '../hooks/useMercadoPago';
import { buildApiUrl } from '../services/api';
import {
  AppliedCoupon,
  createEmptyPaymentFormData,
  PaymentFormData,
  SubscriptionPlan,
  SubscriptionPlanId
} from '../types/payment';
import { maskCPF } from '../utils/documentUtils';
import {
  getCouponDiscountPercent,
  getCouponSummaryText,
  isFreeDaysCoupon,
  normalizeCouponCode,
  parseCouponValidationResponse
} from '../utils/couponUtils';

const isValidCPF = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');

  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const numbers = digits.split('').map(Number);

  const firstSum = numbers
    .slice(0, 9)
    .reduce((sum, num, index) => sum + num * (10 - index), 0);
  let firstCheck = (firstSum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== numbers[9]) return false;

  const secondSum = numbers
    .slice(0, 10)
    .reduce((sum, num, index) => sum + num * (11 - index), 0);
  let secondCheck = (secondSum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;

  return secondCheck === numbers[10];
};

const FALLBACK_PLAN_OPTIONS: SubscriptionPlan[] = [
  {
    id: 'STARTER',
    title: 'Starter',
    subtitle: 'Para profissionais iniciando automações',
    priceCents: 7990,
    features: [
      'Atendimento simultâneo em múltiplos números',
      'Automação de WhatsApp e e-mail para operações enxutas',
      'Setup rápido e experiência intuitiva'
    ]
  },
  {
    id: 'GROWTH',
    title: 'Growth',
    subtitle: 'Ideal para equipes comerciais em expansão',
    priceCents: 9490,
    features: [
      'Automações com maior capacidade operacional',
      'Mais velocidade para campanhas e follow-up',
      'Suporte prioritário e fluxo de crescimento'
    ]
  },
  {
    id: 'SCALE',
    title: 'Scale',
    subtitle: 'Projetado para operações de alta demanda',
    priceCents: 12900,
    features: [
      'Performance otimizada para escala rápida',
      'Fluxos avançados para times de grande volume',
      'Controle robusto e mais estabilidade operacional'
    ]
  }
];

const formatCurrency = (amountCents: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountCents / 100);

const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('pt-BR');
};

export function PaymentPage() {
  const navigate = useNavigate();
  const { paymentNotice, paymentRequired, checkAuth, logout } = useAuth();
  const { createCardToken } = useMercadoPago();

  const [paymentData, setPaymentData] = useState<PaymentFormData>(createEmptyPaymentFormData);
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEncryptingCard, setIsEncryptingCard] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [lastCepLookup, setLastCepLookup] = useState('');
  const [planOptions, setPlanOptions] = useState<SubscriptionPlan[]>(FALLBACK_PLAN_OPTIONS);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanId>('GROWTH');
  const [currentPlanId, setCurrentPlanId] = useState<SubscriptionPlanId | null>(null);
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState<string | null>(null);
  const [canManageSubscription, setCanManageSubscription] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [configReloadToken, setConfigReloadToken] = useState(0);
  const selectedPlanConfig = planOptions.find((plan) => plan.id === selectedPlan) ?? planOptions[0] ?? FALLBACK_PLAN_OPTIONS[1];
  const baseInstallmentCents = selectedPlanConfig.priceCents;
  const appliedDiscountPercent = getCouponDiscountPercent(appliedCoupon);
  const existingDiscountPercent = Math.max(0, Number(paymentNotice?.discountPercent) || 0);
  const displayDiscountPercent = appliedDiscountPercent || existingDiscountPercent;
  const installmentAmountCents = Math.max(0, Math.round(baseInstallmentCents * (1 - displayDiscountPercent / 100)));
  const hasDiscount = displayDiscountPercent > 0;

  const trialEndsAtLabel = useMemo(
    () => formatDate(paymentNotice?.trialEndsAt ?? paymentNotice?.accessEndsAt ?? paymentNotice?.dueAt),
    [paymentNotice?.trialEndsAt, paymentNotice?.accessEndsAt, paymentNotice?.dueAt]
  );

  useEffect(() => {
    let isActive = true;

    const loadPlans = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(buildApiUrl('/payments/config'), {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(payload?.message || 'Não foi possível carregar os planos');
        }

        const plans = Array.isArray(payload?.data?.plans) && payload.data.plans.length > 0
          ? payload.data.plans as SubscriptionPlan[]
          : FALLBACK_PLAN_OPTIONS;
        const currentPlanId = (payload?.data?.currentPlanId as SubscriptionPlanId | undefined)
          || (paymentNotice?.planId as SubscriptionPlanId | undefined)
          || plans[0]?.id
          || 'GROWTH';

        if (!isActive) {
          return;
        }

        setPlanOptions(plans);
        setCurrentPlanId(currentPlanId);
        setCurrentSubscriptionStatus(typeof payload?.data?.currentSubscriptionStatus === 'string' ? payload.data.currentSubscriptionStatus : null);
        setCanManageSubscription(Boolean(payload?.data?.canManageSubscription));
        setSelectedPlan((currentSelectedPlan) => (
          plans.some((plan) => plan.id === currentSelectedPlan) ? currentSelectedPlan : currentPlanId
        ));
      } catch (error) {
        if (isActive) {
          setPlanOptions(FALLBACK_PLAN_OPTIONS);
          setCurrentPlanId(paymentNotice?.planId || null);
          setCurrentSubscriptionStatus(null);
          setCanManageSubscription(false);
          if (paymentNotice?.planId) {
            setSelectedPlan(paymentNotice.planId);
          }
        }
      } finally {
        if (isActive) {
          setIsLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      isActive = false;
    };
  }, [paymentNotice?.planId, configReloadToken]);

  useEffect(() => {
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (cepDigits.length !== 8 || cepDigits === lastCepLookup) {
      return;
    }

    let isActive = true;
    const timer = setTimeout(async () => {
      setIsCepLoading(true);

      try {
        const response = await fetch(buildApiUrl(`/cep/${cepDigits}`));
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (isActive) {
            setPaymentErrors((prev) => ({
              ...prev,
              billingZip: data?.error || 'CEP não encontrado'
            }));
            setLastCepLookup(cepDigits);
          }
          return;
        }

        if (!isActive || !data) {
          return;
        }

        setPaymentData((prev) => ({
          ...prev,
          billingStreet: prev.billingStreet || data.street || '',
          billingDistrict: prev.billingDistrict || data.district || '',
          billingCity: prev.billingCity || data.city || '',
          billingState: prev.billingState || data.state || '',
          billingComplement: prev.billingComplement || data.complement || ''
        }));

        setPaymentErrors((prev) => {
          const next = { ...prev };
          delete next.billingZip;
          delete next.billingStreet;
          delete next.billingDistrict;
          delete next.billingCity;
          delete next.billingState;
          return next;
        });
        setLastCepLookup(cepDigits);
      } catch (error) {
        if (isActive) {
          setPaymentErrors((prev) => ({
            ...prev,
            billingZip: 'Erro ao consultar CEP'
          }));
          setLastCepLookup(cepDigits);
        }
      } finally {
        if (isActive) {
          setIsCepLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [paymentData.billingZip, lastCepLookup]);

  const validatePayment = () => {
    const nextErrors: Record<string, string> = {};
    const cardDigits = paymentData.cardNumber.replace(/\D/g, '');
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (!paymentData.holderName.trim()) nextErrors.holderName = 'Nome do titular é obrigatório';
    if (!isValidCPF(paymentData.holderDocument)) nextErrors.holderDocument = 'CPF do titular inválido';
    if (cardDigits.length < 13) nextErrors.cardNumber = 'Número do cartão inválido';
    if (!/^\d{2}\/\d{2}$/.test(paymentData.cardExpiry)) nextErrors.cardExpiry = 'Validade inválida';
    if (!/^\d{3,4}$/.test(paymentData.cardCvv)) nextErrors.cardCvv = 'CVV inválido';
    if (cepDigits.length < 8) nextErrors.billingZip = 'CEP inválido';
    if (!paymentData.billingStreet.trim()) nextErrors.billingStreet = 'Endereço é obrigatório';
    if (!paymentData.billingNumber.trim()) nextErrors.billingNumber = 'Número é obrigatório';
    if (!paymentData.billingDistrict.trim()) nextErrors.billingDistrict = 'Bairro é obrigatório';
    if (!paymentData.billingCity.trim()) nextErrors.billingCity = 'Cidade é obrigatória';
    if (!/^[A-Za-z]{2}$/.test(paymentData.billingState.trim())) nextErrors.billingState = 'UF inválida';

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleApplyCoupon = async () => {
    const normalized = normalizeCouponCode(couponCode);
    if (!normalized) {
      setCouponError('Informe um cupom válido');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await fetch(buildApiUrl('/coupons/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: normalized })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Cupom inválido');
      }

      const parsedCoupon = parseCouponValidationResponse(data, normalized);
      if (isFreeDaysCoupon(parsedCoupon)) {
        throw new Error('Cupom de dias grátis não pode ser usado nesta tela de pagamento');
      }

      setAppliedCoupon(parsedCoupon);
      setCouponCode(normalized);
      setCouponError('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao validar cupom';
      setCouponError(message);
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleChangePlan = async () => {
    try {
      setIsChangingPlan(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/payments/subscription/change-plan'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan: selectedPlan })
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || 'Não foi possível alterar o plano');
      }

      toast.success(result?.message || 'Plano alterado com sucesso!');
      setCurrentPlanId(selectedPlan);
      await checkAuth();
      setConfigReloadToken((current) => current + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao alterar o plano';
      toast.error(message);
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar a assinatura recorrente?')) {
      return;
    }

    try {
      setIsCancellingSubscription(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/payments/subscription/cancel'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || 'Não foi possível cancelar a assinatura');
      }

      toast.success(result?.message || 'Assinatura cancelada com sucesso!');
      await checkAuth();
      navigate('/login');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao cancelar a assinatura';
      toast.error(message);
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleSubmitPayment = async () => {
    if (!validatePayment()) {
      return;
    }

    let cardTokenId = '';
    try {
      setIsEncryptingCard(true);
      cardTokenId = await createCardToken(paymentData);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao tokenizar cartão';
      toast.error(message);
      return;
    } finally {
      setIsEncryptingCard(false);
    }

    const paymentEndpoint = import.meta.env.VITE_PAYMENT_SUBSCRIBE_ENDPOINT || '/payments/subscribe';

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl(paymentEndpoint), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: selectedPlan,
          holderName: paymentData.holderName,
          holderDocument: paymentData.holderDocument,
          billingZip: paymentData.billingZip,
          billingStreet: paymentData.billingStreet,
          billingNumber: paymentData.billingNumber,
          billingComplement: paymentData.billingComplement,
          billingDistrict: paymentData.billingDistrict,
          billingCity: paymentData.billingCity,
          billingState: paymentData.billingState,
          cardTokenId: cardTokenId,
          couponCode: appliedCoupon?.code
        })
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || result?.error || 'Não foi possível processar o pagamento');
      }

      toast.success(result?.message || 'Pagamento enviado com sucesso!');
      await checkAuth();
      setPaymentData(createEmptyPaymentFormData());
      setPaymentErrors({});
      setAppliedCoupon(null);
      setCouponCode('');
      setCouponError('');

      const requiresPayment = Boolean(result?.data?.paymentNotice?.requiresPayment);
      if (!requiresPayment) {
        navigate('/contatos');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao processar o pagamento';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 p-5 sm:p-8 lg:p-10 space-y-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Regularize seu acesso</h1>
              <p className="text-sm text-gray-600">
                {paymentRequired
                  ? 'Seu período gratuito terminou. Informe os dados do cartão para continuar usando o sistema.'
                  : 'Você pode atualizar seus dados de pagamento abaixo.'}
              </p>
            </div>

            {(paymentNotice?.message || trialEndsAtLabel) && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
                {paymentNotice?.message && (
                  <p>
                    <strong>Aviso:</strong> {paymentNotice.message}
                  </p>
                )}
                {trialEndsAtLabel && (
                  <p>
                    <strong>Data de referência:</strong> {trialEndsAtLabel}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-6">
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Dados do titular</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome impresso no cartão</label>
                  <input
                    type="text"
                    value={paymentData.holderName}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, holderName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderName && (
                    <p className="mt-2 text-sm text-red-600">{paymentErrors.holderName}</p>
                  )}
                  </div>
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CPF do titular</label>
                  <input
                    type="text"
                    value={paymentData.holderDocument}
                    onChange={(e) =>
                      setPaymentData((prev) => ({ ...prev, holderDocument: maskCPF(e.target.value) }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderDocument && (
                    <p className="mt-2 text-sm text-red-600">{paymentErrors.holderDocument}</p>
                  )}
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Cartão</h2>
                <div className="mt-4 space-y-4">
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número do cartão</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    value={paymentData.cardNumber}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                    placeholder="0000 0000 0000 0000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.cardNumber && (
                    <p className="mt-2 text-sm text-red-600">{paymentErrors.cardNumber}</p>
                  )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validade (MM/AA)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      value={paymentData.cardExpiry}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))}
                      placeholder="MM/AA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardExpiry && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardExpiry}</p>
                    )}
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      value={paymentData.cardCvv}
                      onChange={(e) =>
                        setPaymentData((prev) => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))
                      }
                      placeholder="123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardCvv && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardCvv}</p>
                    )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Endereço de cobrança</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={paymentData.billingZip}
                        onChange={(e) => setPaymentData((prev) => ({ ...prev, billingZip: formatCep(e.target.value) }))}
                        placeholder="00000-000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                        style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                        disabled={isSubmitting}
                      />
                      {isCepLoading && (
                        <div className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">Buscando...</div>
                      )}
                    </div>
                    {paymentErrors.billingZip && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingZip}</p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      value={paymentData.billingStreet}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingStreet: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingStreet && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingStreet}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                    <input
                      type="text"
                      value={paymentData.billingNumber}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingNumber: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingNumber && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingNumber}</p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={paymentData.billingComplement}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingComplement: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input
                      type="text"
                      value={paymentData.billingDistrict}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingDistrict: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingDistrict && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingDistrict}</p>
                    )}
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                    <input
                      type="text"
                      value={paymentData.billingCity}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingCity: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingCity && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingCity}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">UF</label>
                    <input
                      type="text"
                      value={paymentData.billingState}
                      onChange={(e) =>
                        setPaymentData((prev) => ({ ...prev, billingState: e.target.value.toUpperCase().slice(0, 2) }))
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingState && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingState}</p>
                    )}
                  </div>
                </div>
              </section>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={logout}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                disabled={isSubmitting || isEncryptingCard}
              >
                Sair
              </button>
              <button
                type="button"
                onClick={handleSubmitPayment}
                disabled={isSubmitting || isEncryptingCard}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
              >
                {isEncryptingCard
                  ? 'Tokenizando cartão...'
                  : isSubmitting
                    ? 'Processando...'
                    : 'Confirmar pagamento'}
              </button>
            </div>
          </div>

          <aside className="border-t border-gray-200 bg-gray-50 p-5 sm:p-8 lg:border-l lg:border-t-0 lg:p-8">
            <div className="space-y-5 lg:sticky lg:top-8">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  Plano da assinatura
                  {isLoadingPlans ? ' carregando...' : ''}
                </p>
                <p className="mt-1 text-xs text-gray-500">Os valores abaixo são carregados da configuração do backend.</p>
                {currentPlanId && (
                  <p className="mt-2 text-xs text-gray-500">
                    Plano atual: <strong>{planOptions.find((plan) => plan.id === currentPlanId)?.title || currentPlanId}</strong>
                    {currentSubscriptionStatus ? ` • status: ${currentSubscriptionStatus}` : ''}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                {planOptions.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    disabled={isSubmitting || isEncryptingCard}
                    className={`w-full rounded-xl border p-3 text-left transition-colors ${
                      selectedPlan === plan.id
                        ? 'border-[#233e4f] bg-slate-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{plan.title}</p>
                        <p className="mt-1 text-xs text-gray-500">{plan.subtitle}</p>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">R$ {formatCurrency(plan.priceCents)}/mês</span>
                    </div>
                  </button>
                ))}
              </div>
              {canManageSubscription && currentPlanId && currentPlanId !== selectedPlan && (
                <button
                  type="button"
                  onClick={handleChangePlan}
                  disabled={isChangingPlan || isSubmitting || isEncryptingCard}
                  className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
                >
                  {isChangingPlan ? 'Atualizando plano...' : `Alterar para ${selectedPlanConfig.title}`}
                </button>
              )}
              {canManageSubscription && !paymentRequired && (
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={isCancellingSubscription || isSubmitting || isEncryptingCard}
                  className="w-full rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 transition-all hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isCancellingSubscription ? 'Cancelando assinatura...' : 'Cancelar assinatura'}
                </button>
              )}
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-2">
              <p className="text-sm text-gray-500">{selectedPlanConfig.title} LeadNexia</p>
              <p className="text-2xl font-semibold text-gray-900">R$ {formatCurrency(installmentAmountCents)}/mês</p>
              {hasDiscount && (
                <p className="text-xs text-gray-400 line-through">
                  R$ {formatCurrency(baseInstallmentCents)}/mês
                </p>
              )}
              <p className="text-sm text-gray-500">Cobrança recorrente mensal</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
              <label className="block text-sm font-medium text-gray-700">Cupom de desconto</label>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    const nextValue = normalizeCouponCode(e.target.value);
                    setCouponCode(nextValue);
                    setCouponError('');
                    if (appliedCoupon && appliedCoupon.code !== nextValue) {
                      setAppliedCoupon(null);
                    }
                  }}
                  placeholder="Insira o cupom"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                  disabled={isSubmitting || isApplyingCoupon || Boolean(existingDiscountPercent)}
                />
                <button
                  type="button"
                  onClick={appliedCoupon ? handleRemoveCoupon : handleApplyCoupon}
                  disabled={
                    isSubmitting ||
                    isApplyingCoupon ||
                    Boolean(existingDiscountPercent) ||
                    (!couponCode.trim() && !appliedCoupon)
                  }
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
                >
                  {appliedCoupon ? 'Remover' : (isApplyingCoupon ? 'Validando...' : 'Aplicar')}
                </button>
              </div>
              {couponError && (
                <p className="text-sm text-red-600">{couponError}</p>
              )}
              {appliedCoupon && (
                <p className="text-sm text-emerald-700">{getCouponSummaryText(appliedCoupon)}</p>
              )}
              {existingDiscountPercent > 0 && !appliedCoupon && (
                <p className="text-sm text-emerald-700">
                  Desconto já aplicado: {existingDiscountPercent}%.
                </p>
              )}
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <strong>Importante:</strong> após o envio, o pagamento poderá precisar de confirmação interna.
            </div>
            <div className="text-xs text-gray-500">
              Ao continuar, você autoriza a cobrança recorrente mensal no valor informado.
            </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
